import {
  formatCents,
  formatCount,
  formatDecimal,
  formatTime,
  parseMoneyToCents,
  tt,
  type CourierSettlement,
} from '@agos/shared'

/**
 * The external courier's app - `FRONTEND.md` §9.786, on BACKEND.md §10A.438, owner O139.
 *
 * The owner: *"hneď píše v apke šoférovi odmenu za objednávku"* and *"čaká na šoféra ... toto zobrazovať len
 * externým šoférom"*. These are the rules his screen draws from: the orders waiting for a courier, the ones he has,
 * when each will be ready, what pickup means for money, and what is owed between him and the restaurant.
 *
 * Money arrives as decimal euros from the server and is held here in whole cents.
 */

export const POOL_PATH = '/api/v1/driver-pool'
export const MINE_PATH = '/api/v1/driver-pool/mine'
export const BALANCE_PATH = '/api/v1/employees/my-courier-balance'

export function claimPath(orderId: number): string {
  return `/api/v1/driver-pool/${String(orderId)}/claim`
}

export function releasePath(orderId: number): string {
  return `/api/v1/driver-pool/${String(orderId)}/release`
}

/** The courier's three reads, one key each, so a claim refreshes exactly what it moved. */
export const POOL_KEY = ['driver-pool'] as const
export const MINE_KEY = ['driver-pool', 'mine'] as const
export const BALANCE_KEY = ['courier-balance', 'mine'] as const

/** How often the pool is asked again - a push brings him here sooner, and this keeps the list honest without it. */
export const POOL_POLL_MS = 15_000

export type PickupState = 'waiting' | 'cooking' | 'ready'

export interface Pickup {
  state: PickupState
  /** For `cooking`: acceptance plus the restaurant's minutes for the order's size. */
  readyAt: string | null
  minutes: number | null
}

export interface CourierOrder {
  id: number
  orderNumber: string
  status: string
  address: string
  km: number | null
  rewardCents: number | null
  kitchenPieces: number
  pickup: Pickup
  paymentMethod: string
  totalCents: number
  /** Positive: he pays the restaurant this at pickup. Negative: the restaurant pays him. */
  settleCents: number
  claimedAt: string | null
  /** Null until the order is his - the street before claiming, the person after. */
  customerName: string | null
  customerPhone: string | null
  notes: string | null
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

function decimal(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value)
  return null
}

function centsOrNull(value: unknown): number | null {
  return decimal(value) === null ? null : parseMoneyToCents(value as number | string)
}

function pickupOf(value: unknown): Pickup {
  const row = typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {}
  const state = row.state === 'cooking' || row.state === 'ready' ? row.state : 'waiting'
  const minutes = decimal(row.minutes)
  return { state, readyAt: text(row.readyAt), minutes: minutes === null ? null : Math.round(minutes) }
}

/** The pool or his orders, or null when the answer is not a list - which the screen reports rather than draws empty. */
export function courierOrdersOf(payload: unknown): CourierOrder[] | null {
  if (!Array.isArray(payload)) return null
  return payload
    .filter((one): one is Record<string, unknown> => typeof one === 'object' && one !== null)
    .filter((one) => typeof one.id === 'number')
    .map((one) => ({
      id: one.id as number,
      orderNumber: text(one.orderNumber) ?? String(one.id),
      status: text(one.status) ?? '',
      address: text(one.address) ?? '',
      km: decimal(one.km),
      rewardCents: centsOrNull(one.reward),
      kitchenPieces: Math.max(0, Math.round(decimal(one.kitchenPieces) ?? 0)),
      pickup: pickupOf(one.pickup),
      paymentMethod: text(one.paymentMethod) ?? '',
      totalCents: parseMoneyToCents(decimal(one.total) ?? 0),
      settleCents: parseMoneyToCents(decimal(one.settle) ?? 0),
      claimedAt: text(one.claimedAt),
      customerName: text(one.customerName),
      customerPhone: text(one.customerPhone),
      notes: text(one.notes),
    }))
}

/** *Čaká na prijatie do kuchyne* / *Pripravené okolo 18:40 (do 15 min)* / *Pripravené - môžete ísť*. */
export function pickupNote(pickup: Pickup): string {
  if (pickup.state === 'ready') return tt('Pripravené - môžete ísť')
  if (pickup.state === 'waiting') return tt('Čaká na prijatie do kuchyne')
  if (pickup.readyAt === null) return tt('V kuchyni')
  const around = `${tt('Pripravené okolo')} ${formatTime(pickup.readyAt)}`
  return pickup.minutes === null ? around : `${around} (${tt('do')} ${formatCount(pickup.minutes)} min)`
}

/**
 * What pickup means for money, from the courier's side: *Pri vyzdvihnutí zaplatíte 20,40 €* for a cash order he
 * collects at the door, *Prevádzka vám vyplatí 3,50 €* for one paid online - the owner's two cases.
 */
export function settleNote(order: Pick<CourierOrder, 'settleCents'>): string {
  if (order.settleCents > 0) return `${tt('Pri vyzdvihnutí zaplatíte')} ${formatCents(order.settleCents)}`
  if (order.settleCents < 0) return `${tt('Prevádzka vám vyplatí')} ${formatCents(-order.settleCents)}`
  return tt('Pri vyzdvihnutí sa nič neplatí')
}

/** *3,2 km · odmena 4,10 €* - what a courier decides on, beside the street. */
export function offerLine(order: Pick<CourierOrder, 'km' | 'rewardCents' | 'kitchenPieces'>): string {
  const parts: string[] = []
  if (order.km !== null) parts.push(`${formatDecimal(order.km, 1)} km`)
  if (order.rewardCents !== null) parts.push(`${tt('odmena')} ${formatCents(order.rewardCents)}`)
  parts.push(`${formatCount(order.kitchenPieces)} ${tt('ks z kuchyne')}`)
  return parts.join(' · ')
}

// ─── What is owed ────────────────────────────────────────────────────────

/* §9.786 - the balance reader is `@agos/shared`'s, because the board's settling dialog draws the same figure from
   the restaurant's side. Re-exported so this module's callers are untouched by where it lives. */
export { courierBalanceOf, type CourierBalance } from '@agos/shared'

/** *Prevádzke dlžíte 11,00 €* / *Prevádzka vám dlží 4,50 €* / *Ste vyrovnaní*. */
export function balanceNote(owesCents: number): string {
  if (owesCents > 0) return `${tt('Prevádzke dlžíte')} ${formatCents(owesCents)}`
  if (owesCents < 0) return `${tt('Prevádzka vám dlží')} ${formatCents(-owesCents)}`
  return tt('Ste vyrovnaní')
}

/** A settlement from his side: *Zaplatili ste 11,00 €* or *Dostali ste 4,50 €*. */
export function settlementNote(settlement: Pick<CourierSettlement, 'amountCents'>): string {
  return settlement.amountCents > 0
    ? `${tt('Zaplatili ste')} ${formatCents(settlement.amountCents)}`
    : `${tt('Dostali ste')} ${formatCents(-settlement.amountCents)}`
}

/** Why a claim failed, in the server's words when it sent some - *Objednávku si už vzal iný šofér*. */
export const TAKEN_CODE = 'ALREADY_CLAIMED'

/**
 * The door question's answers for an external courier - the owner's answer: he carries no terminal, so the customer
 * pays him cash or the QR he shows; *Karta kuriér* is not his to offer, and the server refuses it from him.
 */
export function courierDoorChoices(choices: readonly string[]): readonly string[] {
  return choices.filter((method) => method !== 'card_courier' && method !== 'card_terminal')
}
